import React from "react";
import { ChevronRight, Plus } from "lucide-react";
import { useNexOs } from "@/lib/nexOs";
import { useLabel, Section } from "@/lib/dynamicEngine";
import { useNdsEditor, useTileHandlers } from "@/lib/ndsEditor";

/**
 * Tier 2 — 10-Step Revenue Flow (2×5 grid on desktop).
 * Every card is clickable → opens <StepCoachingModal> with coaching content
 * on top and the actual records at that stage below.
 *
 * The status badge on each card comes from context.stepBadges — updated on
 * mode/vertical switch via a single /step-counts round-trip.
 */
export default function TenStepRevenueFlow() {
  const { currentVertical, stepBadges, mode } = useNexOs();
  const { fireTileAction } = useNdsEditor();
  const kicker = useLabel("nds.steps.kicker", "// 10-STEP REVENUE FLOW · UNIVERSAL LIFECYCLE");
  const heading = useLabel("nds.steps.heading", "Every dollar. Every step. Every vertical.");

  const steps = currentVertical?.steps || [];

  const openCoaching = (step) => fireTileAction({
    kind: "step_coaching",
    step,
    verticalId: currentVertical?.id,
  });

  return (
    <section data-testid="nds-ten-step" className="flex flex-col gap-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="flex flex-col gap-2">
          <div className="nds-label-dim">{kicker}</div>
          <h2 className="text-nex-silver text-2xl md:text-3xl font-serif tracking-tight">
            {heading}
          </h2>
        </div>
        {/* + New Brief · opens the friction-audit intake as a floating popup
            (in-console flow, no redirect to the marketing page). */}
        <button
          type="button"
          onClick={() => fireTileAction({ kind: "brief_intake", title: "New Friction Audit Brief" })}
          data-testid="nds-new-brief-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-nex-gold/50 text-nex-gold font-mono text-[0.7rem] tracking-[0.22em] uppercase hover:bg-nex-gold hover:text-nex-navy transition-colors self-start md:self-auto"
        >
          <Plus className="w-4 h-4" /> New Brief
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {steps.map((step) => {
          const liveBadge = stepBadges?.[step.id];
          const badgeText = liveBadge && liveBadge !== "—"
            ? liveBadge
            : (step.status_badge || `Open · Score ${step.score || 10}`);
          const hasLiveCount = mode === "live" && liveBadge && /^[1-9]/.test(liveBadge);
          const editTarget = currentVertical ? { kind: "step", verticalId: currentVertical.id, itemId: step.id } : null;
          return (
            <Section scope="nds_step" id={step.id} key={step.id}>
              <StepCard
                step={step}
                badgeText={badgeText}
                hasLiveCount={hasLiveCount}
                editTarget={editTarget}
                onOpenCoaching={() => openCoaching(step)}
              />
            </Section>
          );
        })}
        {steps.length === 0 && (
          <div className="col-span-5 nds-card p-8 text-center font-mono text-sm text-nex-navy" data-testid="nds-steps-empty">
            Loading steps…
          </div>
        )}
      </div>
    </section>
  );
}

function StepCard({ step, badgeText, hasLiveCount, editTarget, onOpenCoaching }) {
  const { openTileEditor } = useNdsEditor();
  const tileHandlers = useTileHandlers({ target: editTarget, onEdit: openTileEditor });
  return (
    <button
      type="button"
      className="nds-step w-full text-left transition-all duration-200"
      data-testid={`nds-step-${step.id}`}
      onClick={onOpenCoaching}
      {...tileHandlers}
      title="Click to open coaching · Right-click to edit"
    >
      <span className="nds-step__bar" />
      <span className="nds-step__num">STEP {step.id}</span>
      <div className="font-mono text-[0.65rem] tracking-[0.22em] text-nex-navy/50 uppercase mb-3">
        {step.id}
      </div>
      <div className="font-serif text-[1.05rem] leading-snug font-semibold pr-8">
        {step.title}
      </div>
      <div className="mt-5 flex items-center gap-2 text-nex-navy/70">
        <span
          className={`nds-pill ${hasLiveCount ? "nds-pill--gold" : "nds-pill--live"}`}
          style={{ padding: "3px 8px", fontSize: "0.6rem" }}
          data-testid={`nds-step-badge-${step.id}`}
        >
          {badgeText}
        </span>
        <ChevronRight className="w-4 h-4 ml-auto text-nex-navy/40 group-hover:text-nex-navy" />
      </div>
    </button>
  );
}
